import { AlertCircle, CloudOff, ShieldCheck, UserPlus, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SeverityBadge, DefectStatusBadge } from "@/components/ui/status-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProfileAvatar } from "@/components/common/ProfileAvatar";
import { getProfileDisplayName } from "@/lib/profileUtils";
import { DefectAssignableUserItems } from "./DefectAssignableUserItems";
import type { AssignableUser, DefectCardData } from "./DefectCard.types";

interface DefectCardBadgesProps {
  defect: DefectCardData;
  isOverdue: boolean;
  assignableUsers: AssignableUser[];
  onAssign?: (id: string, userId: string | null) => void;
}

export function DefectCardBadges({
  defect,
  isOverdue,
  assignableUsers,
  onAssign,
}: DefectCardBadgesProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 ml-10 sm:ml-0">
      <SeverityBadge severity={defect.severity} />
      <DefectStatusBadge status={defect.status} />
      <TeamBadge teamName={defect.team_name} />
      <VerifierBadge defect={defect} />
      <OverdueBadge isOverdue={isOverdue} />
      <OfflineBadge isOfflinePending={defect.isOfflinePending} />
      <UnavailableAssigneeBadge
        defect={defect}
        assignableUsers={assignableUsers}
        onAssign={onAssign}
      />
    </div>
  );
}

function TeamBadge({ teamName }: { teamName?: string | null }) {
  if (!teamName) return null;

  return (
    <Badge variant="outline" className="text-xs gap-1">
      <Users className="h-3 w-3" />
      {teamName}
    </Badge>
  );
}

function VerifierBadge({ defect }: { defect: DefectCardData }) {
  const { t } = useTranslation();
  if (
    !defect.verifying_qm_profile ||
    (defect.status !== "resolved" && defect.status !== "closed")
  ) {
    return null;
  }

  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
      data-sentry-mask
    >
      <ProfileAvatar profile={defect.verifying_qm_profile} size="xs" />
      <ShieldCheck className="h-3 w-3" />
      {getProfileDisplayName(defect.verifying_qm_profile, t("common.unknown"))}
    </span>
  );
}

function OverdueBadge({ isOverdue }: { isOverdue: boolean }) {
  const { t } = useTranslation();
  if (!isOverdue) return null;

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-destructive/10 text-destructive border border-destructive/20">
      <AlertCircle className="h-3 w-3" />
      {t("dashboard.overdue")}
    </span>
  );
}

function OfflineBadge({ isOfflinePending }: { isOfflinePending?: boolean }) {
  const { t } = useTranslation();
  if (!isOfflinePending) return null;

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border"
      title={t("common.willSyncWhenOnline")}
    >
      <CloudOff className="h-3 w-3" />
      {t("common.savedOffline")}
    </span>
  );
}

interface UnavailableAssigneeBadgeProps {
  defect: DefectCardData;
  assignableUsers: AssignableUser[];
  onAssign?: (id: string, userId: string | null) => void;
}

function UnavailableAssigneeBadge({
  defect,
  assignableUsers,
  onAssign,
}: UnavailableAssigneeBadgeProps) {
  if (!defect.isAssigneeUnavailable) {
    return null;
  }

  if (!defect.assigned_to) {
    return null;
  }

  return (
    <div className="inline-flex items-center gap-2 flex-wrap">
      <UnavailableAssigneeReason hasProfile={Boolean(defect.assigned_to_profile)} />
      <ReassignDropdown defect={defect} assignableUsers={assignableUsers} onAssign={onAssign} />
    </div>
  );
}

function UnavailableAssigneeReason({ hasProfile }: { hasProfile: boolean }) {
  const { t } = useTranslation();
  const label = hasProfile ? t("tasks.userDisabled") : t("tasks.userDeleted");

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
      <AlertCircle className="h-3 w-3" />
      {label}
    </span>
  );
}

function ReassignDropdown({ defect, assignableUsers, onAssign }: UnavailableAssigneeBadgeProps) {
  const { t } = useTranslation();

  if (!onAssign) {
    return null;
  }

  const canReassign = [
    assignableUsers.length > 0,
    ["open", "in_progress"].includes(defect.status),
  ].every(Boolean);

  if (!canReassign) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs gap-1 text-warning hover:text-warning hover:bg-warning/10"
        >
          <UserPlus className="h-3 w-3" />
          {t("tasks.reassign")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
        <DefectAssignableUserItems
          defectId={defect.id}
          assignableUsers={assignableUsers}
          onAssign={onAssign}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
